import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  List, ListItem, ListItemButton, ListItemText, useTheme, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getChapters, getVerses } from '../services/bibleApi';
import { bibleBooks, getBookNameById } from '../utils/books';

const SingleVerseDialog = ({ open, onClose, selectedBible = null, onAddVerse }) => {
  const theme = useTheme();

  // State
  const [pickerBook, setPickerBook] = useState('GEN');
  const [pickerChapter, setPickerChapter] = useState(1);
  const [pickerVerse, setPickerVerse] = useState(1);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [availableVerses, setAvailableVerses] = useState([]);

  useEffect(() => {
    if (open) {
      // Reset state when opening
      setPickerBook('GEN');
      setPickerChapter(1);
      setPickerVerse(1);
      loadChaptersForBook();
    }
  }, [open]);

  useEffect(() => {
    if (pickerBook && selectedBible) {
      loadChaptersForBook();
    }
  }, [pickerBook, selectedBible]);

  useEffect(() => {
    if (pickerBook && pickerChapter && selectedBible) {
      loadVersesForChapter();
    }
  }, [pickerBook, pickerChapter, selectedBible]);

  const loadChaptersForBook = async () => {
    try {
      const chapters = await getChapters(selectedBible, pickerBook);
      setAvailableChapters(chapters);
      if (chapters.length > 0 && (!pickerChapter || pickerChapter > chapters.length)) {
        setPickerChapter(1);
      }
    } catch (error) {
      console.warn('Error loading chapters:', error);
      setAvailableChapters(Array.from({ length: pickerBook === 'PSA' ? 150 : pickerBook === 'MAT' ? 28 : 50 }, (_, i) => ({ id: (i + 1).toString(), number: i + 1 })));
    }
  };

  const loadVersesForChapter = async () => {
    try {
      const verses = await getVerses(selectedBible, `${pickerBook}.${pickerChapter}`);
      setAvailableVerses(verses);
      if (verses.length > 0 && (!pickerVerse || pickerVerse > verses.length)) {
        setPickerVerse(1);
      }
    } catch (error) {
      console.warn('Error loading verses:', error);
      setAvailableVerses(Array.from({ length: 30 }, (_, i) => ({ id: (i + 1).toString(), number: i + 1 })));
    }
  };

  const handleBookSelect = (bookId) => {
    setPickerBook(bookId);
    setPickerChapter(1);
    setPickerVerse(1);
  };

  const handleChapterSelect = (chapterNum) => {
    setPickerChapter(chapterNum);
    setPickerVerse(1);
  };

  const handleVerseSelect = (verseNum) => {
    setPickerVerse(verseNum);
  };

  const handleConfirm = () => {
    const bookName = getBookNameById(pickerBook);
    const reference = `${bookName} ${pickerChapter}:${pickerVerse}`;
    onAddVerse(reference);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.shape.borderRadius * 2,
          background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
          border: `1px solid ${theme.palette.outline.light}`,
          boxShadow: theme.shadows[8],
          height: '80vh',
          maxHeight: '600px'
        }
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${theme.palette.outline.light}`,
          pb: 2,
          color: theme.palette.text.primary,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h6">Add Verse to Practice</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>

        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', gap: 1, borderRadius: theme.shape.borderRadius, border: `1px solid ${theme.palette.outline.light}` }}>
          {/* Books Column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${theme.palette.outline.light}` }}>
            <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
              Book
            </Typography>
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {bibleBooks.map((book) => (
                <ListItem key={book.id} disablePadding>
                  <ListItemButton
                    selected={book.id === pickerBook}
                    onClick={() => handleBookSelect(book.id)}
                    sx={{
                      py: 1,
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      },
                    }}
                  >
                    <ListItemText primary={book.name} sx={{ textAlign: 'center', m: 0 }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Chapters Column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: `1px solid ${theme.palette.outline.light}` }}>
            <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
              Chapter
            </Typography>
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {availableChapters.map((chapter) => (
                <ListItem key={chapter.id} disablePadding>
                  <ListItemButton
                    selected={chapter.number === pickerChapter}
                    onClick={() => handleChapterSelect(chapter.number)}
                    sx={{
                      py: 1,
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      },
                    }}
                  >
                    <ListItemText primary={chapter.number} sx={{ textAlign: 'center', m: 0 }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Verses Column */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ p: 2, pb: 1, fontWeight: 600, textAlign: 'center', bgcolor: theme.palette.background.paper }}>
              Verse
            </Typography>
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {availableVerses.map((verse) => (
                <ListItem key={verse.id} disablePadding>
                  <ListItemButton
                    selected={verse.number === pickerVerse}
                    onClick={() => handleVerseSelect(verse.number)}
                    sx={{
                      py: 1,
                      px: 2,
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      },
                    }}
                  >
                    <ListItemText primary={verse.number} sx={{ textAlign: 'center', m: 0 }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: `1px solid ${theme.palette.outline.light}`, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            borderRadius: theme.shape.borderRadius,
            textTransform: 'none',
            fontWeight: 600,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            px: 4,
            py: 1,
            borderRadius: theme.shape.borderRadius * 1.5,
            textTransform: 'none',
            fontWeight: 600,
            background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: theme.shadows[2],
            '&:hover': {
              background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: theme.shadows[4],
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabled,
              boxShadow: 'none',
              transform: 'none'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Add Verse
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SingleVerseDialog;
